// app/lib/chat-stream.ts — een EventSource die tegen de echte wereld kan.
//
// Puur transport: geen React, geen kennis van chatberichten. De stores
// (chat-visitor.ts, chat-agent.ts) hangen hier hun eigen betekenis aan.
//
// Vier dingen die een kale `new EventSource(url)` niet doet en die je in
// productie stuk voor stuk nodig hebt:
//
//   1. EIGEN BACKOFF. De ingebouwde herverbinding van de browser respecteert wel
//      `retry:`, maar geeft geen controle en stopt niet als het netwerk weg is.
//   2. EEN CURSOR DIE MEEGAAT. De browser stuurt Last-Event-ID mee bij zijn
//      EIGEN herverbinding, maar niet wanneer wij na close() een nieuwe
//      EventSource bouwen. Omdat wij de backoff zelf doen, dragen we de cursor
//      zelf mee als ?since=.
//   3. EEN WATCHDOG. Een proxy die de socket half sluit levert een verbinding op
//      die "open" heet maar niets meer doet. Zonder watchdog lijkt de widget dan
//      voor eeuwig verbonden. De server stuurt elke 15 s een echt `ping`-event
//      (geen SSE-commentaar — dat vuurt aan clientzijde geen handler af).
//   4. RUST BIJ EEN VERBORGEN TAB. Een SSE-verbinding houdt een socket vast;
//      een telefoon in je zak zou hem uren openhouden.

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'retrying' | 'offline' | 'closed';

export interface StreamEvent {
  type: string;
  data: unknown;
  id: string | null;
}

export interface ChatStreamHandle {
  close(): void;
  status(): StreamStatus;
  /** Meteen opnieuw proberen, zonder de backoff af te wachten. */
  reconnectNow(): void;
}

export interface ChatStreamOptions {
  /** Zonder cursor; die wordt per (her)verbinding aangeplakt. */
  url: string;
  /** De laatst verwerkte cursor. Wordt bij ÉLKE verbinding opnieuw gelezen. */
  since?: () => number | null;
  /** Welke gebeurtenissen we willen ontvangen. */
  events: readonly string[];
  onEvent: (event: StreamEvent) => void;
  onStatus?: (status: StreamStatus) => void;
  /** Sluiten na zoveel ms onzichtbaar. 0 = nooit sluiten. */
  hiddenGraceMs?: number;
}

const NOOP: ChatStreamHandle = {
  close() {},
  status: () => 'idle',
  reconnectNow() {},
};

/** Stilte waarna we de verbinding niet meer vertrouwen. Ruim boven de 15 s
 *  serverping, zodat één gemiste tik geen herverbinding uitlokt. */
const WATCHDOG_MS = 45_000;
const MAX_BACKOFF_MS = 30_000;

export function createChatStream(opts: ChatStreamOptions): ChatStreamHandle {
  // Deze functie wordt alleen uit een effect aangeroepen, maar de module wordt
  // wél op de server geïmporteerd. Goedkope verzekering.
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') return NOOP;

  const hiddenGraceMs = opts.hiddenGraceMs ?? 60_000;

  let source: EventSource | null = null;
  let status: StreamStatus = 'idle';
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  let watchdog: ReturnType<typeof setInterval> | null = null;
  let lastTraffic = Date.now();
  let disposed = false;

  const setStatus = (next: StreamStatus) => {
    if (status === next) return;
    status = next;
    opts.onStatus?.(next);
  };

  const clearRetry = () => {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  function disconnect() {
    if (source) {
      source.close();
      source = null;
    }
    if (watchdog) {
      clearInterval(watchdog);
      watchdog = null;
    }
  }

  function connect() {
    if (disposed) return;
    clearRetry();
    disconnect();
    setStatus('connecting');

    const cursor = opts.since?.() ?? null;
    const url = cursor !== null && cursor > 0
      ? `${opts.url}${opts.url.includes('?') ? '&' : '?'}since=${cursor}`
      : opts.url;

    const es = new EventSource(url, { withCredentials: true });
    source = es;
    lastTraffic = Date.now();

    const handle = (type: string) => (raw: MessageEvent<string>) => {
      lastTraffic = Date.now();
      // Pas hier terugzetten, niet in onopen: een proxy kan een verbinding
      // openen en meteen laten vallen, en dan wordt "resetten bij open" een
      // strakke herverbindingslus die het probleem verergert.
      attempt = 0;
      setStatus('open');
      if (type === 'ping') return; // alleen om de watchdog te voeden
      let data: unknown = null;
      try {
        data = JSON.parse(raw.data) as unknown;
      } catch {
        return;
      }
      opts.onEvent({ type, data, id: raw.lastEventId || null });
    };

    for (const type of opts.events) es.addEventListener(type, handle(type) as EventListener);
    es.addEventListener('ping', handle('ping') as EventListener);

    es.onerror = () => {
      if (disposed) return;
      // EventSource meldt geen HTTP-status. Sluiten en zelf opnieuw proberen.
      disconnect();
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setStatus('offline');
        return; // wachten op het online-event; blijven proberen kost alleen batterij
      }
      scheduleRetry();
    };

    watchdog = setInterval(() => {
      if (Date.now() - lastTraffic > WATCHDOG_MS) {
        // "Open" maar muisstil: de verbinding is dood zonder het te melden.
        disconnect();
        scheduleRetry();
      }
    }, 5_000);
  }

  function scheduleRetry() {
    if (disposed) return;
    setStatus('retrying');
    const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt);
    // ±20% spreiding, zodat vijftig tabbladen na een serverherstart niet
    // allemaal op dezelfde milliseconde terugkomen.
    const jitter = base * 0.2 * (Math.random() * 2 - 1);
    attempt++;
    clearRetry();
    retryTimer = setTimeout(connect, Math.max(500, base + jitter));
  }

  /* ─────────────────────  reageren op de buitenwereld  ──────────────── */

  const onOnline = () => {
    attempt = 0;
    connect();
  };
  const onOffline = () => {
    disconnect();
    clearRetry();
    setStatus('offline');
  };

  const onVisibility = () => {
    if (document.hidden) {
      // Niet meteen sluiten: een tabwissel van twee seconden mag geen lopend
      // gesprek onderbreken. Wel na een tijdje, want een socket in je broekzak
      // is zonde.
      if (hiddenGraceMs > 0 && !hiddenTimer) {
        hiddenTimer = setTimeout(() => {
          hiddenTimer = null;
          if (document.hidden) {
            disconnect();
            clearRetry();
            setStatus('idle');
          }
        }, hiddenGraceMs);
      }
      return;
    }
    if (hiddenTimer) {
      clearTimeout(hiddenTimer);
      hiddenTimer = null;
    }
    if (!source) {
      attempt = 0;
      connect(); // met de cursor, dus zonder gemiste berichten
    }
  };

  // pagehide en niet beforeunload: dat laatste sloopt de bfcache en Safari
  // vuurt het onbetrouwbaar af.
  const onPageHide = () => {
    disconnect();
    clearRetry();
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibility);

  connect();

  return {
    close() {
      disposed = true;
      clearRetry();
      if (hiddenTimer) clearTimeout(hiddenTimer);
      disconnect();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      setStatus('closed');
    },
    status: () => status,
    reconnectNow() {
      attempt = 0;
      connect();
    },
  };
}
